import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import * as css from './SeatmapManage.css';
import {SMAP_DOMAIN} from '../../../config/config';
import {getSeatmapState, saveSeatmapTicketMap} from '../../../redux/event/eventActions';
import {getEventTickets} from '../../../redux/tickets/ticketsActions';
import {bounceMessage} from '../../../layout/userMessages/UserMessage';

// Management screen for a NEW-format seatmap event (/e/:eid/tickets/seats).
// The map itself — areas, seats, price categories, blocking (חסום) and holds
// (מוחזק/שיריונים) — is managed in the Seatmap Studio, embedded here. The
// "שיוך כרטיסים" tab attaches the event's ticket types to the map's price
// categories (category → tid), the NEW-format equivalent of the legacy
// area/seat ticket attachment.
function SeatmapManage({smid, eid}) {
    const tickets = useSelector(s => s.tickets.tickets);
    const [tab, setTab] = useState('map');
    const [state, setState] = useState(null);
    const [attach, setAttach] = useState({}); // categoryId -> [tid, ...]
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        getEventTickets();
        getSeatmapState(eid).then(s => {
            setState(s);
            const m = {};
            (s?.ticketMap || []).forEach(({categoryId, tid}) => {
                (m[categoryId] = m[categoryId] || []).push(tid);
            });
            setAttach(m);
        });
    }, [eid]);

    const activeTickets = (tickets || []).filter(tk => tk && !tk.draft);
    const categories = state?.categories || [];

    const toggle = (categoryId, tid) => {
        setAttach(prev => {
            const cur = new Set(prev[categoryId] || []);
            if(cur.has(tid)) cur.delete(tid); else cur.add(tid);
            return {...prev, [categoryId]: [...cur]};
        });
    };

    const save = async () => {
        setSaving(true);
        const entries = [];
        Object.entries(attach).forEach(([categoryId, tids]) =>
            (tids || []).forEach(tid => entries.push({categoryId, tid})));
        try {
            await saveSeatmapTicketMap(entries, eid);
            bounceMessage('השיוך נשמר', 'success');
        } catch {
            bounceMessage('שמירת השיוך נכשלה', 'attention');
        } finally {
            setSaving(false);
        }
    };

    return (
        <css.wrap>
            <css.tabs>
                <css.tab className={tab === 'map' ? 'active' : ''} onClick={() => setTab('map')}>ניהול מפה</css.tab>
                <css.tab className={tab === 'tickets' ? 'active' : ''} onClick={() => setTab('tickets')}>שיוך כרטיסים</css.tab>
            </css.tabs>

            {tab === 'map' &&
                <css.frame>
                    <iframe title="seatmap-studio" src={`${SMAP_DOMAIN}/?mid=${smid}`}
                        allow="clipboard-read; clipboard-write" />
                </css.frame>}

            {tab === 'tickets' &&
                <css.panel>
                    <css.hint>בחרו אילו סוגי כרטיסים משויכים לכל קטגוריית מחיר במפה. חסימת מושבים ושיריונים מתבצעים בלשונית ״ניהול מפה״ (סטטוס מושב: חסום / מוחזק).</css.hint>
                    {!categories.length && <css.empty>למפה זו אין עדיין קטגוריות מחיר. הגדירו קטגוריות בלשונית ״ניהול מפה״.</css.empty>}
                    {categories.map(cat => (
                        <css.catRow key={cat.id}>
                            <css.catHead>
                                <css.swatch style={{background: cat.color || '#ccc'}} />
                                <span className="name">{cat.name || cat.id}</span>
                                {cat.price != null && <span className="price">{`₪${cat.price}`}</span>}
                            </css.catHead>
                            <css.ticketList>
                                {!activeTickets.length && <css.empty>אין כרטיסים פעילים באירוע.</css.empty>}
                                {activeTickets.map(tk => {
                                    const on = (attach[cat.id] || []).includes(tk.tid);
                                    return (
                                        <css.ticketChip key={tk.tid} className={on ? 'on' : ''}
                                            onClick={() => toggle(cat.id, tk.tid)}>
                                            {tk.title}
                                        </css.ticketChip>
                                    );
                                })}
                            </css.ticketList>
                        </css.catRow>
                    ))}
                    <css.actions>
                        <css.saveBtn disabled={saving} onClick={save}>{saving ? 'שומר…' : 'שמירה'}</css.saveBtn>
                    </css.actions>
                </css.panel>}
        </css.wrap>
    );
}

export default SeatmapManage;
